import * as duckdb from "@duckdb/duckdb-wasm";
import eh_worker from "@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url";
import mvp_worker from "@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url";
import duckdb_wasm_eh from "@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url";
import duckdb_wasm from "@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url";
import { authenticatedFetch } from "./auth/auth";

type DB = duckdb.AsyncDuckDB;

const BUNDLES: duckdb.DuckDBBundles = {
  mvp: {
    mainModule: duckdb_wasm,
    mainWorker: mvp_worker,
  },
  eh: {
    mainModule: duckdb_wasm_eh,
    mainWorker: eh_worker,
  },
};

let poolPromise: Promise<DB[]> | null = null;

// Respect Vite base path so assets resolve under subpaths
const BASE_URL = import.meta.env.BASE_URL ?? "/";
const LOCAL_SCHEMA_URL = `${BASE_URL}db/schema.sql`;
const LOCAL_LOAD_URL = `${BASE_URL}db/load.sql`;
const LOCAL_PARQUET_URL = `${BASE_URL}db/aggregated_data.parquet`;

// Pool configuration
const POOL_SIZE = 4; // Number of parallel workers

interface ParquetBuffers {
  aggregatedData: Uint8Array;
}

async function createDBInstance(useRemote: boolean, buffers?: ParquetBuffers): Promise<DB> {
  const bundle = await duckdb.selectBundle(BUNDLES);
  const worker = new Worker(bundle.mainWorker!);
  const logger = new duckdb.VoidLogger();
  const db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);

  const conn = await db.connect();

  // Load schema if present (optional) — skip if missing or HTML fallback
  try {
    const schemaResponse = await fetch(LOCAL_SCHEMA_URL);
    if (schemaResponse.ok) {
      const ct = schemaResponse.headers.get("content-type") ?? "";
      const text = await schemaResponse.text();
      if (ct.includes("text/html") || text.trimStart().startsWith("<!")) {
        console.log("[DuckDB] schema.sql looks like HTML; skipping");
      } else if (text.trim().length > 0) {
        await conn.query(text);
        console.log("[DuckDB] Applied schema.sql");
      } else {
        console.log("[DuckDB] schema.sql empty; skipping");
      }
    } else {
      console.log("[DuckDB] schema.sql not found; skipping explicit schema");
    }
  } catch {
    console.log("[DuckDB] Skipping schema.sql (not found or fetch error)");
  }

  // Register parquet files (timing the download/load)
  const downloadStart = performance.now();

  if (buffers) {
    await db.registerFileBuffer("db/aggregated_data.parquet", buffers.aggregatedData);
    console.log(
      `[DuckDB] Parquet buffers provided directly, skipping fetch. (duration: ${(
        performance.now() - downloadStart
      ).toFixed(0)}ms)`,
    );
  } else {
    let parquetResponse: Response;
    if (useRemote) {
      const remoteParquetUrl = import.meta.env.VITE_GCS_PARQUET_URL as string;
      parquetResponse = await authenticatedFetch(remoteParquetUrl);
    } else {
      parquetResponse = await fetch(LOCAL_PARQUET_URL);
    }
    if (!parquetResponse.ok) {
      throw new Error(`[DuckDB] Failed to fetch parquet: ${parquetResponse.status} ${parquetResponse.statusText}`);
    }
    const buffer = await parquetResponse.arrayBuffer();
    console.log(
      `[DuckDB] Downloaded parquet data (${buffer.byteLength} bytes) in ${(performance.now() - downloadStart).toFixed(
        0,
      )}ms`,
    );
    const registerStart = performance.now();
    await db.registerFileBuffer("db/aggregated_data.parquet", new Uint8Array(buffer));
    console.log(`[DuckDB] Registered parquet buffer in ${(performance.now() - registerStart).toFixed(0)}ms`);
  }

  // Load/ingest SQL — guard against missing files or HTML fallbacks
  const loadResponse = await fetch(LOCAL_LOAD_URL);
  if (!loadResponse.ok) {
    console.warn(`[DuckDB] load.sql not found (${loadResponse.status}); falling back to default parquet load`);
    await conn.query(
      "CREATE OR REPLACE TABLE aggregated_data AS SELECT * FROM read_parquet('db/aggregated_data.parquet', union_by_name=true);",
    );
  } else {
    const ct = loadResponse.headers.get("content-type") ?? "";
    const text = await loadResponse.text();
    if (ct.includes("text/html") || text.trimStart().startsWith("<!")) {
      console.warn("[DuckDB] load.sql response is HTML; using default parquet load");
      await conn.query(
        "CREATE OR REPLACE TABLE aggregated_data AS SELECT * FROM read_parquet('db/aggregated_data.parquet', union_by_name=true);",
      );
    } else {
      await conn.query(text);
    }
  }

  // Timing index creation
  const indexStart = performance.now();
  await Promise.all([
    conn.query("CREATE INDEX IF NOT EXISTS idx_group_type ON aggregated_data(group_type)"),
    conn.query("CREATE INDEX IF NOT EXISTS idx_bid_date ON aggregated_data(bid_date)"),
    conn.query("CREATE INDEX IF NOT EXISTS idx_group_type_bid_date ON aggregated_data(group_type, bid_date)"),
    conn.query("CREATE INDEX IF NOT EXISTS idx_dim_key1 ON aggregated_data(dim_key1)"),
    conn.query("CREATE INDEX IF NOT EXISTS idx_dim_val1 ON aggregated_data(dim_val1)"),
  ]);
  console.log(`[DuckDB] Created aggregated_data indexes in ${(performance.now() - indexStart).toFixed(0)}ms`);

  await conn.close();

  return db;
}

async function initDBPool(useRemote: boolean): Promise<DB[]> {
  if (!poolPromise) {
    poolPromise = (async () => {
      // Load parquet data once
      let parquetData: ArrayBuffer;

      if (useRemote) {
        const remoteParquetUrl = import.meta.env.VITE_GCS_PARQUET_URL as string;
        const parquetResponse = await authenticatedFetch(remoteParquetUrl);
        if (!parquetResponse.ok) {
          throw new Error(`[DuckDB] Failed to fetch parquet: ${parquetResponse.status} ${parquetResponse.statusText}`);
        }
        parquetData = await parquetResponse.arrayBuffer();
      } else {
        const parquetResponse = await fetch(LOCAL_PARQUET_URL);
        if (!parquetResponse.ok) {
          throw new Error(`[DuckDB] Failed to fetch parquet: ${parquetResponse.status} ${parquetResponse.statusText}`);
        }
        parquetData = await parquetResponse.arrayBuffer();
      }

      // Create pool of DB instances sequentially to avoid buffer detachment issues
      // Each instance needs its own copy of the parquet buffer
      const pool: DB[] = await Promise.all(
        Array.from({ length: POOL_SIZE }, async () => {
          const buffers: ParquetBuffers = {
            aggregatedData: new Uint8Array(parquetData.slice(0)),
          };
          return createDBInstance(useRemote, buffers);
        }),
      );

      console.log(`[DuckDB] Connection pool initialized with ${POOL_SIZE} workers`);
      return pool;
    })();
  }
  return poolPromise;
}

// Simple round-robin pool manager
class DBPool {
  private pool: DB[];
  private currentIndex = 0;

  constructor(pool: DB[]) {
    this.pool = pool;
  }

  // Get next DB instance in round-robin fashion
  getDB(): DB {
    const db = this.pool[this.currentIndex];
    this.currentIndex = (this.currentIndex + 1) % this.pool.length;
    return db;
  }

  // Register a file buffer in all pool workers so read_parquet() works on any worker.
  // Each worker gets its own copy because postMessage transfers (detaches) the ArrayBuffer.
  async registerFileBuffer(name: string, buffer: Uint8Array): Promise<void> {
    const copies = this.pool.map(() => new Uint8Array(buffer));
    await Promise.all(this.pool.map((db, i) => db.registerFileBuffer(name, copies[i])));
  }

  // Execute queries in parallel using the pool
  async executeParallel<T>(queries: (() => Promise<T>)[]): Promise<T[]> {
    return Promise.all(queries.map(query => query()));
  }
}

let dbPoolInstance: DBPool | null = null;

async function getDBPool(useRemote: boolean): Promise<DBPool> {
  if (!dbPoolInstance) {
    const pool = await initDBPool(useRemote);
    dbPoolInstance = new DBPool(pool);
  }
  return dbPoolInstance;
}

export { type DB, type DBPool, getDBPool };
